import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    } else {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 300);
    }
  };
  const goTo = (path: string) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };
  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-212" data-mp="jkt1mqaq6zst">
      <nav aria-label="Main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-213" data-mp="3n19itkx01bqe">
        <div className="flex justify-between items-center" data-id="element-214" data-mp="3s2ytgzgb7ndy">
          {/* Logo */}
          <button type="button" onClick={handleLogoClick} aria-label="KittyPot, go to homepage" className="flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-215" data-mp="vdz0rfe62mb">
            <img src="https://cdn.magicpatterns.com/uploads/piF3tygLKnzoL2Q5y3TGTn/LOGO_-_5000pxc.png" alt="" className="h-24 w-24 lg:h-28 lg:w-28 object-contain rounded-full" data-id="element-216" data-mp="39bfwb98yv3ju" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-217" data-mp="3b0te3pdqyzyf">
            <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-kitty-dark font-medium transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-218" data-mp="3b0b7bytgl3c9">
              How it works
            </button>
            <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-kitty-dark font-medium transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-219" data-mp="2dfoyuiz4b1zv">
              Features
            </button>
            <button onClick={() => goTo('/faq')} className="text-gray-600 hover:text-kitty-dark font-medium transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-220" data-mp="2l9or1g761on">
              FAQ
            </button>
            <button onClick={() => goTo('/about')} className="text-gray-600 hover:text-kitty-dark font-medium transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-221" data-mp="f1is478a3cog">
              About
            </button>
            <button onClick={() => goTo('/contact')} className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-5 py-2.5 rounded-full font-semibold transition-colors duration-150 shadow-sm hover:shadow-md active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-dark" data-id="element-222" data-mp="sxd2yvsm4c47">
              Contact us
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden p-2 text-gray-600 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-223" data-mp="1gl4evya8qn4y">
            {isMobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" data-id="element-224" data-mp="3b2fkif9youvj" /> : <Menu className="w-6 h-6" aria-hidden="true" data-id="element-225" data-mp="3b4iipml9pubc" />}
          </button>
        </div>
      </nav>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-226" data-mp="6no56mt3y5j3">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="md:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-227" data-mp="6qacclc36j0s">
            <div className="px-6 pt-4 pb-8 flex flex-col gap-2" data-id="element-228" data-mp="pmjxfsleo9ua">
              <button onClick={() => scrollToSection('how-it-works')} className="text-left py-3.5 px-2 text-gray-700 font-medium text-lg active:bg-gray-50 rounded-xl transition-colors" data-id="element-229" data-mp="pkz60bue170n">
                How it works
              </button>
              <button onClick={() => scrollToSection('features')} className="text-left py-3.5 px-2 text-gray-700 font-medium text-lg active:bg-gray-50 rounded-xl transition-colors" data-id="element-230" data-mp="2hccmdi5axzdm">
                Features
              </button>
              <button onClick={() => goTo('/faq')} className="text-left py-3.5 px-2 text-gray-700 font-medium text-lg active:bg-gray-50 rounded-xl transition-colors" data-id="element-231" data-mp="2w5xwab2fq76z">
                FAQ
              </button>
              <button onClick={() => goTo('/about')} className="text-left py-3.5 px-2 text-gray-700 font-medium text-lg active:bg-gray-50 rounded-xl transition-colors" data-id="element-232" data-mp="39zfgly2y3ny2">
                About
              </button>
              <button onClick={() => goTo('/contact')} className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-5 py-4 rounded-xl font-semibold text-center text-lg mt-3 active:scale-[0.98] transition-colors duration-150" data-id="element-233" data-mp="1eto7v0c4t1mp">
                Contact us
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}